import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { platformsAPI } from '../../api';
import './SideIcons.css';

// Helper to compare platform arrays (order-independent)
const arraysEqual = (a, b) => {
  if (!a || !b || a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
};

// Platform icon configuration
export const PLATFORM_ICONS = {
  reddit: { src: '/icons/reddit.svg', color: '#FF4500', fallback: '🔴' },
  tiktok: { src: '/icons/tiktok.svg', color: '#000000', fallback: '🎵' },
  instagram: { src: '/icons/instagram.svg', color: '#E4405F', fallback: '📷' },
  instagram_via_facebook: { src: '/icons/instagram.svg', color: '#E4405F', fallback: '📷' },
  facebook: { src: '/icons/facebook.svg', color: '#1877F2', fallback: '📘' },
  twitter: { src: '/icons/twitter.svg', color: '#1DA1F2', fallback: '🐦' },
  youtube: { src: '/icons/youtube.svg', color: '#FF0000', fallback: '▶️' },
  linkedin: { src: '/icons/linkedin.svg', color: '#0A66C2', fallback: '💼' }
};

const SideIcons = ({ 
  onPlatformToggle, 
  activePlatforms = null,
  requireCapability = null // e.g., 'can_fetch_posts', 'can_publish', 'inbox'
}) => {
  const [connections, setConnections] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const hasAutoSelectedRef = useRef(false);
  const selectedPlatformsRef = useRef(new Set());
  const lastSentPlatformsRef = useRef(null); // Track last sent platforms to avoid redundant calls

  const hasCapability = useCallback((connection) => {
    if (!connection) return false;
    const caps = connection.capabilities || {};
    if (requireCapability === 'inbox') {
      return caps.can_messages || caps.can_comment || caps.can_mentions;
    }
    if (!requireCapability) return true;
    return !!caps[requireCapability];
  }, [requireCapability]);

  useEffect(() => {
    (async () => {
      try {
        const data = await platformsAPI.getConnections();
        setConnections(data);

        // Auto-select all platforms with required capability
        const allowed = data.filter(c => hasCapability(c)).map(c => c.platform);
        const newSelected = new Set(allowed);
        setSelectedPlatforms(newSelected);
        selectedPlatformsRef.current = newSelected;
        hasAutoSelectedRef.current = true;

        // Notify parent so pages can detect "no supported platforms" and black out
        // Only call if platforms actually changed
        const platformsArray = Array.from(newSelected);
        if (onPlatformToggle && !arraysEqual(lastSentPlatformsRef.current, platformsArray)) {
          lastSentPlatformsRef.current = platformsArray;
          onPlatformToggle(platformsArray);
        }
      } catch (error) {
        console.error('Error loading connections:', error);
      } finally {
        setLoading(false);
      }
    })();

    // Listen for connection changes from Settings - update UI directly from the
    // event payload, no additional API call. Only a single getConnections() call
    // is made on page load.
    const onAdded = (e) => {
      const { platform } = e.detail;
      // Use the connection data provided by the event (set by Settings); fall back
      // to a minimal entry if none was passed.
      const conn = e.detail.connection || { platform };
      setConnections(prev => {
        if (prev.find(c => c.platform === platform)) return prev;
        return [...prev, conn];
      });
      const allowed = hasCapability(conn);
      if (allowed) {
        const newSelected = new Set([...selectedPlatformsRef.current, platform]);
        setSelectedPlatforms(newSelected);
        selectedPlatformsRef.current = newSelected;
        const platformsArray = Array.from(newSelected);
        if (onPlatformToggle && !arraysEqual(lastSentPlatformsRef.current, platformsArray)) {
          lastSentPlatformsRef.current = platformsArray;
          onPlatformToggle(platformsArray);
        }
      }
    };

    const onRemoved = (e) => {
      const { platform } = e.detail;
      setConnections(prev => prev.filter(c => c.platform !== platform));
      const newSelected = new Set(selectedPlatformsRef.current);
      newSelected.delete(platform);
      setSelectedPlatforms(newSelected);
      selectedPlatformsRef.current = newSelected;
      const platformsArray = Array.from(newSelected);
      // Reset lastSentPlatformsRef so disconnect always triggers callback (even if same array)
      lastSentPlatformsRef.current = null;
      if (onPlatformToggle && !arraysEqual(lastSentPlatformsRef.current, platformsArray)) {
        lastSentPlatformsRef.current = platformsArray;
        onPlatformToggle(platformsArray);
      }
    };

    window.addEventListener('connection-added', onAdded);
    window.addEventListener('connection-removed', onRemoved);
    return () => {
      window.removeEventListener('connection-added', onAdded);
      window.removeEventListener('connection-removed', onRemoved);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCapability]); // onPlatformToggle is stable via useCallback in parent; we use refs to track values

  useEffect(() => {
    if (activePlatforms !== null) {
      setSelectedPlatforms(new Set(activePlatforms));
    }
  }, [activePlatforms]);

  const handlePlatformClick = (platform) => {
    const connection = connections.find(c => c.platform === platform);
    
    if (!connection) {
      // Platform not connected - go to settings to connect
      navigate('/settings');
      return;
    }

    // If connected but lacking required capability, do nothing
    if (!hasCapability(connection)) return;
    
    // Toggle platform selection for connected platforms
    const newSelected = new Set(selectedPlatforms);
    if (newSelected.has(platform)) {
      newSelected.delete(platform);
    } else {
      newSelected.add(platform);
    }
    
    setSelectedPlatforms(newSelected);
    selectedPlatformsRef.current = newSelected;
    
    // Notify parent of selection change - only if actually changed
    const platformsArray = Array.from(newSelected);
    if (onPlatformToggle && !arraysEqual(lastSentPlatformsRef.current, platformsArray)) {
      lastSentPlatformsRef.current = platformsArray;
      onPlatformToggle(platformsArray);
    }
  };

  const handleAddProfile = () => {
    navigate('/settings');
  };

  // Get connected platform names for quick lookup
  const connectedPlatforms = new Set(connections.map(c => c.platform));

  // Hide regular instagram when instagram_via_facebook is connected (recommended implementation)
  const hasInstagramViaFacebook = connectedPlatforms.has('instagram_via_facebook');
  const displayPlatforms = connections
    .filter(c => !(c.platform === 'instagram' && hasInstagramViaFacebook))
    .map(c => c.platform);

  return (
    <aside className="side-icons">
      {loading ? (
        <div className="icon loading">
          <span className="loading-dot"></span>
        </div>
      ) : (
        displayPlatforms.map((platform) => {
          const connection = connections.find(c => c.platform === platform);
          const iconConfig = PLATFORM_ICONS[platform] || { fallback: '?' };
          const isConnected = !!connection;
          const allowed = isConnected && hasCapability(connection);
          const isSelected = selectedPlatforms.has(platform);
          
          return (
            <div 
              key={platform} 
              data-platform={platform}
              className={`icon ${isConnected ? 'connected' : 'disconnected'} ${isSelected ? 'selected' : ''} ${allowed ? '' : 'locked'}`}
              onClick={() => handlePlatformClick(platform)}
              title={isConnected 
                ? `${platform} - @${connection.platform_username || connection.platform_display_name}${allowed ? (isSelected ? ' (Active)' : ' (Click to toggle)') : ' (Not available for this account type)'}`
                : `${platform} - Not connected (Click to connect)`
              }
            >
              {/* Try to load SVG icon, fallback to emoji */}
              <img 
                src={iconConfig.src} 
                alt={platform}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <span className="icon-fallback" style={{ display: 'none' }}>
                {iconConfig.fallback}
              </span>
              
              {/* Selection indicator */}
              {isConnected && allowed && isSelected && (
                <span className="selected-indicator">✓</span>
              )}

              {/* Locked badge */}
              {isConnected && !allowed && (
                <span className="locked-indicator">🔒</span>
              )}
              
              {/* Avatar overlay for connected accounts */}
              {connection?.platform_avatar_url && (
                <img 
                  src={connection.platform_avatar_url}
                  alt=""
                  className="avatar-overlay"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
            </div>
          );
        })
      )}
      
      <div 
        className="add-profile" 
        onClick={handleAddProfile}
        title="Connect new account"
      >
        +
      </div>
    </aside>
  );
};

export default SideIcons;
